import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";
import { OrderItem } from "../models/IOrders";
import { orderStatuses } from "../utils/Util";

function getTask(item: OrderItem): number {
  return orderStatuses[item.orderStatusId]?.task ?? -1;
}

interface StatCardProps {
  icon: string;
  label: string;
  value: string;
  sub?: string;
  bg: string;
  color: string;
  border: string;
}

function StatCard({ icon, label, value, sub, bg, color, border }: StatCardProps) {
  return (
    <div
      className="p-4 rounded-3 h-100"
      style={{ background: bg, border: `1.5px solid ${border}` }}
    >
      <div className="d-flex align-items-center gap-2 mb-2">
        <span style={{ fontSize: '1.4rem', color }}><i className={`bi ${icon}`}></i></span>
        <span className="text-muted fw-semibold" style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      </div>
      <div className="fw-bold" style={{ fontSize: '1.75rem', color, lineHeight: 1 }}>{value}</div>
      {sub && <div className="mt-1 text-muted" style={{ fontSize: '0.8rem' }}>{sub}</div>}
    </div>
  );
}

const STATUS_COLORS = {
  active:    '#f59e0b',
  delivered: '#22c55e',
  cancelled: '#ef4444',
};

function OrderReport({ orders }: { orders: OrderItem[] }) {
  if (orders.length === 0) {
    return (
      <div className="text-center py-5 text-muted">
        <i className="bi bi-bar-chart" style={{ fontSize: '2.5rem', opacity: 0.3 }}></i>
        <div className="mt-2 small">Rapor oluşturmak için yeterli sipariş yok.</div>
      </div>
    );
  }

  const active    = orders.filter(o => { const t = getTask(o); return t !== 1 && t !== 4; });
  const delivered = orders.filter(o => getTask(o) === 1);
  const cancelled = orders.filter(o => getTask(o) === 4);

  const totalRevenue     = orders.reduce((s, o) => s + (o.orderPrice ?? 0), 0);
  const deliveredRevenue = delivered.reduce((s, o) => s + (o.orderPrice ?? 0), 0);
  const avgOrderValue    = totalRevenue / orders.length;
  const deliveryRate     = orders.length > 0 ? (delivered.length / orders.length) * 100 : 0;

  const barData = [
    { name: 'Aktif',   adet: active.length },
    { name: 'Teslim',  adet: delivered.length },
    { name: 'İptal',   adet: cancelled.length },
  ];

  const pieData = [
    { name: 'Aktif',   value: active.length,    color: STATUS_COLORS.active    },
    { name: 'Teslim',  value: delivered.length,  color: STATUS_COLORS.delivered },
    { name: 'İptal',   value: cancelled.length,  color: STATUS_COLORS.cancelled },
  ].filter(d => d.value > 0);

  const barColors = [STATUS_COLORS.active, STATUS_COLORS.delivered, STATUS_COLORS.cancelled];

  return (
    <div>
      {/* ── Stat Kartları ──────────────────────────────────────────────── */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <StatCard
            icon="bi-receipt-cutoff"
            label="Toplam Sipariş"
            value={String(orders.length)}
            sub={`${active.length} aktif · ${delivered.length} teslim`}
            bg="#eff6ff" color="#1d4ed8" border="#bfdbfe"
          />
        </div>
        <div className="col-6 col-md-3">
          <StatCard
            icon="bi-cash-coin"
            label="Toplam Ciro"
            value={`${totalRevenue.toFixed(2)} ₺`}
            sub={`Teslim: ${deliveredRevenue.toFixed(2)} ₺`}
            bg="#f0fdf4" color="#166534" border="#bbf7d0"
          />
        </div>
        <div className="col-6 col-md-3">
          <StatCard
            icon="bi-percent"
            label="Teslim Oranı"
            value={`%${deliveryRate.toFixed(1)}`}
            sub={`${delivered.length} / ${orders.length} sipariş`}
            bg="#fffbeb" color="#92400e" border="#fde68a"
          />
        </div>
        <div className="col-6 col-md-3">
          <StatCard
            icon="bi-basket2-fill"
            label="Ort. Sepet"
            value={`${avgOrderValue.toFixed(2)} ₺`}
            sub={`${cancelled.length} sipariş iptal`}
            bg="#fef2f2" color="#991b1b" border="#fecaca"
          />
        </div>
      </div>

      {/* ── Grafikler ─────────────────────────────────────────────────── */}
      <div className="row g-3">

        {/* Bar Chart */}
        <div className="col-md-7">
          <div
            className="p-3 rounded-3 h-100"
            style={{ background: '#fff', border: '1.5px solid #e5e7eb' }}
          >
            <div className="fw-semibold mb-3" style={{ color: '#374151', fontSize: '0.82rem' }}>
              <i className="bi bi-bar-chart-fill me-2 text-primary"></i>
              SİPARİŞ DURUM DAĞILIMI
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={barData} barSize={48}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 13, fontWeight: 600 }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip
                  cursor={{ fill: '#f9fafb' }}
                  formatter={(val) => [`${val} sipariş`, 'Adet']}
                  contentStyle={{ borderRadius: '10px', border: '1px solid #e5e7eb', fontSize: '0.82rem' }}
                />
                <Bar dataKey="adet" radius={[8, 8, 0, 0]}>
                  {barData.map((_, i) => (
                    <Cell key={i} fill={barColors[i]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pie Chart */}
        <div className="col-md-5">
          <div
            className="p-3 rounded-3 h-100"
            style={{ background: '#fff', border: '1.5px solid #e5e7eb' }}
          >
            <div className="fw-semibold mb-3" style={{ color: '#374151', fontSize: '0.82rem' }}>
              <i className="bi bi-pie-chart-fill me-2 text-warning"></i>
              ORAN DAĞILIMI
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(val, name) => [`${val} sipariş`, name]}
                  contentStyle={{ borderRadius: '10px', border: '1px solid #e5e7eb', fontSize: '0.82rem' }}
                />
                <Legend
                  iconType="circle"
                  iconSize={10}
                  wrapperStyle={{ fontSize: '0.82rem' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>
    </div>
  );
}

export default OrderReport;